import React, { useState } from 'react';
import { FaPlus, FaTrash, FaChevronUp, FaChevronDown } from 'react-icons/fa';

const CHECKLIST_FORM_OPTIONS = [
  { key: 'florida', label: 'Florida (Default)' },
  { key: 'wyoming', label: 'Wyoming' },
  { key: 'newJersey', label: 'New Jersey' },
  { key: 'delawareLLC', label: 'Delaware LLC' },
  { key: 'delawareCCorp', label: 'Delaware C-Corp' },
];

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'number', label: 'Number' },
];

const inputClass = 'w-full text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-2 text-gray-900 dark:text-white';
const labelClass = 'block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1';
const selectClass = `${inputClass} cursor-pointer`;

const Field = ({ label, value, onChange, multiline = false, type = 'text' }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {multiline ? (
      <textarea className={inputClass} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input type={type} className={inputClass} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const slugifyKey = (label, existingKeys = []) => {
  let base = String(label || 'field')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'field';
  let key = base;
  let i = 1;
  while (existingKeys.includes(key)) {
    key = `${base}_${i}`;
    i += 1;
  }
  return key;
};

const collectFieldKeys = (sections = []) => {
  const keys = [];
  sections.forEach((section) => {
    if (section.type === 'select' && section.key) keys.push(section.key);
    (section.fields || []).forEach((f) => {
      if (f.key) keys.push(f.key);
    });
  });
  return keys;
};

const emptyField = (order, existingKeys) => ({
  key: slugifyKey(`custom_field_${order + 1}`, existingKeys),
  label: 'New Field',
  type: 'text',
  multiline: false,
  fullWidth: false,
  required: false,
  order,
  isActive: true,
});

const emptyFieldsSection = (order) => ({
  type: 'fields',
  title: `Section ${order + 1}`,
  order,
  isActive: true,
  fields: [emptyField(0, [])],
});

const AnnualReportChecklistEditor = ({ form, setForm }) => {
  const [activeFormKey, setActiveFormKey] = useState('florida');
  const checklist = form.checklists?.[activeFormKey] || { title: '', subtitle: '', sections: [] };

  const updateChecklist = (field, value) => {
    setForm((prev) => ({
      ...prev,
      checklists: {
        ...prev.checklists,
        [activeFormKey]: { ...prev.checklists?.[activeFormKey], [field]: value },
      },
    }));
  };

  const updateShell = (field, value) => {
    setForm((prev) => ({
      ...prev,
      checklistShell: { ...prev.checklistShell, [field]: value },
    }));
  };

  const mutateSections = (mutator) => {
    setForm((prev) => {
      const sections = mutator([...(prev.checklists?.[activeFormKey]?.sections || [])]);
      return {
        ...prev,
        checklists: {
          ...prev.checklists,
          [activeFormKey]: {
            ...prev.checklists?.[activeFormKey],
            sections: sections.map((section, index) => ({ ...section, order: index })),
          },
        },
      };
    });
  };

  const updateSection = (sectionIndex, field, value) => {
    mutateSections((sections) => {
      sections[sectionIndex] = { ...sections[sectionIndex], [field]: value };
      return sections;
    });
  };

  const updateField = (sectionIndex, fieldIndex, field, value) => {
    mutateSections((sections) => {
      const fields = [...(sections[sectionIndex]?.fields || [])];
      fields[fieldIndex] = { ...fields[fieldIndex], [field]: value };
      sections[sectionIndex] = { ...sections[sectionIndex], fields };
      return sections;
    });
  };

  const addSection = () => {
    mutateSections((sections) => {
      const existingKeys = collectFieldKeys(sections);
      const newSection = emptyFieldsSection(sections.length);
      newSection.fields = [emptyField(0, existingKeys)];
      sections.push(newSection);
      return sections;
    });
  };

  const removeSection = (sectionIndex) => {
    if (!window.confirm('Remove this section and all its fields?')) return;
    mutateSections((sections) => sections.filter((_, i) => i !== sectionIndex));
  };

  const moveSection = (sectionIndex, direction) => {
    mutateSections((sections) => {
      const next = sectionIndex + direction;
      if (next < 0 || next >= sections.length) return sections;
      [sections[sectionIndex], sections[next]] = [sections[next], sections[sectionIndex]];
      return sections;
    });
  };

  const addField = (sectionIndex) => {
    mutateSections((sections) => {
      const existingKeys = collectFieldKeys(sections);
      const fields = [...(sections[sectionIndex]?.fields || [])];
      fields.push(emptyField(fields.length, existingKeys));
      sections[sectionIndex] = { ...sections[sectionIndex], fields };
      return sections;
    });
  };

  const removeField = (sectionIndex, fieldIndex) => {
    mutateSections((sections) => {
      const fields = (sections[sectionIndex]?.fields || []).filter((_, i) => i !== fieldIndex);
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        fields: fields.map((f, i) => ({ ...f, order: i })),
      };
      return sections;
    });
  };

  const moveField = (sectionIndex, fieldIndex, direction) => {
    mutateSections((sections) => {
      const fields = [...(sections[sectionIndex]?.fields || [])];
      const next = fieldIndex + direction;
      if (next < 0 || next >= fields.length) return sections;
      [fields[fieldIndex], fields[next]] = [fields[next], fields[fieldIndex]];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        fields: fields.map((f, i) => ({ ...f, order: i })),
      };
      return sections;
    });
  };

  return (
    <div className="space-y-4">
      <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200">
        Add, remove, and reorder checklist fields here. Changes appear on the user Annual Report form after you save.
      </div>

      <div className="grid md:grid-cols-3 gap-4 p-4 rounded-xl bg-gray-50 dark:bg-dark-900/40">
        <Field label="Back button" value={form.checklistShell?.backLabel} onChange={(v) => updateShell('backLabel', v)} />
        <Field label="Save draft button" value={form.checklistShell?.saveDraftLabel} onChange={(v) => updateShell('saveDraftLabel', v)} />
        <Field label="Submit button" value={form.checklistShell?.submitLabel} onChange={(v) => updateShell('submitLabel', v)} />
      </div>

      <div className="flex flex-wrap gap-2">
        {CHECKLIST_FORM_OPTIONS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setActiveFormKey(option.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeFormKey === option.key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Form title" value={checklist.title} onChange={(v) => updateChecklist('title', v)} />
        <div className="md:col-span-2">
          <Field label="Form subtitle" value={checklist.subtitle} onChange={(v) => updateChecklist('subtitle', v)} multiline />
        </div>
      </div>

      <div className="space-y-4">
        {(checklist.sections || []).map((section, sectionIndex) => (
          <div key={`section-${sectionIndex}`} className="p-4 rounded-xl border border-gray-100 dark:border-dark-700 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                Section {sectionIndex + 1}: {section.type}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={section.isActive !== false}
                    onChange={(e) => updateSection(sectionIndex, 'isActive', e.target.checked)}
                  />
                  Active
                </label>
                <button type="button" onClick={() => moveSection(sectionIndex, -1)} className="p-1.5 rounded border text-gray-500 hover:text-primary-600" title="Move up">
                  <FaChevronUp className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => moveSection(sectionIndex, 1)} className="p-1.5 rounded border text-gray-500 hover:text-primary-600" title="Move down">
                  <FaChevronDown className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => removeSection(sectionIndex)} className="p-1.5 rounded border text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Remove section">
                  <FaTrash className="w-3 h-3" />
                </button>
              </div>
            </div>

            <Field label="Section title" value={section.title} onChange={(v) => updateSection(sectionIndex, 'title', v)} />

            {section.type === 'directorList' && (
              <>
                <Field label="Add button label" value={section.addLabel} onChange={(v) => updateSection(sectionIndex, 'addLabel', v)} />
                <Field label="Item label prefix" value={section.itemLabelPrefix} onChange={(v) => updateSection(sectionIndex, 'itemLabelPrefix', v)} />
              </>
            )}

            {section.type === 'select' && (
              <Field label="Select question label" value={section.label} onChange={(v) => updateSection(sectionIndex, 'label', v)} multiline />
            )}

            {(section.fields || []).length > 0 && (
              <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-dark-700">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fields</p>
                  <button
                    type="button"
                    onClick={() => addField(sectionIndex)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
                  >
                    <FaPlus className="w-3 h-3" /> Add Field
                  </button>
                </div>

                {section.fields.map((fieldItem, fieldIndex) => (
                  <div key={`${fieldItem.key}-${fieldIndex}`} className="p-3 rounded-lg bg-gray-50 dark:bg-dark-900/40 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                        Field {fieldIndex + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => moveField(sectionIndex, fieldIndex, -1)} className="p-1 rounded border text-gray-500" title="Move up">
                          <FaChevronUp className="w-3 h-3" />
                        </button>
                        <button type="button" onClick={() => moveField(sectionIndex, fieldIndex, 1)} className="p-1 rounded border text-gray-500" title="Move down">
                          <FaChevronDown className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeField(sectionIndex, fieldIndex)}
                          className="p-1 rounded border text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Remove field"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <Field
                        label="Field label"
                        value={fieldItem.label}
                        onChange={(v) => updateField(sectionIndex, fieldIndex, 'label', v)}
                      />
                      <Field
                        label="Field key (unique)"
                        value={fieldItem.key}
                        onChange={(v) => updateField(sectionIndex, fieldIndex, 'key', v.replace(/\s+/g, '_'))}
                      />
                      <div>
                        <label className={labelClass}>Field type</label>
                        <select
                          className={selectClass}
                          value={fieldItem.type || 'text'}
                          onChange={(e) => updateField(sectionIndex, fieldIndex, 'type', e.target.value)}
                        >
                          {FIELD_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-xs">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={fieldItem.required === true}
                          onChange={(e) => updateField(sectionIndex, fieldIndex, 'required', e.target.checked)}
                        />
                        Required
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={fieldItem.multiline === true}
                          onChange={(e) => updateField(sectionIndex, fieldIndex, 'multiline', e.target.checked)}
                        />
                        Multiline
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={fieldItem.fullWidth === true}
                          onChange={(e) => updateField(sectionIndex, fieldIndex, 'fullWidth', e.target.checked)}
                        />
                        Full width
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={fieldItem.isActive !== false}
                          onChange={(e) => updateField(sectionIndex, fieldIndex, 'isActive', e.target.checked)}
                        />
                        Active
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addSection}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-primary-300 text-sm font-semibold text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
      >
        <FaPlus className="w-3 h-3" /> Add Section
      </button>
    </div>
  );
};

export default AnnualReportChecklistEditor;
